import React, { useEffect, useMemo, useState, useCallback, useContext } from "react";
import {
  Container,
  Grid,
  Typography,
  Box,
  Card,
  useMediaQuery,
  IconButton,
  Tooltip,
  Modal,
  Button,
  Tabs,
  Tab,
  Paper,
  Stack,
  CircularProgress,
  Skeleton,
  TextField,
  InputAdornment,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import DownloadIcon from "@mui/icons-material/Download";
import DescriptionIcon from "@mui/icons-material/Description";
import ImageIcon from "@mui/icons-material/Image";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import SearchIcon from "@mui/icons-material/Search";
import AssignmentIcon from "@mui/icons-material/Assignment";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ArchitectureIcon from "@mui/icons-material/Architecture";
import InsightsIcon from "@mui/icons-material/Insights";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useNavigate, useSearchParams } from "react-router-dom";

import api, { API_BASE_URL } from "../utils/api";
import ProfileCard from "../components/OwnerDashboard/ProfileCard";
import WorkOrders from "../components/OwnerDashboard/WorkOrders";
import ViolationSummary from "../components/OwnerDashboard/ViolationSummary";
import ArchitecturalRequests from "../components/OwnerDashboard/ArchitecturalRequests";
import { ModulesContext, isModuleActive } from "../utils/ModulesContext";
import SharedStatCard from "./shared/StatCard";

// ─── Pure helpers (no component state dependency) ────────────────────────────

const CLOSED_WO_STATUSES = new Set(["resolved", "closed", "completed"]);
const CLOSED_VIOLATION_STATUSES = new Set(["closed", "violation cured", "withdrawn / canceled"]);

const buildPropertyAddress = (property) => {
  if (!property) return "";
  return [property.street_1, property.street_2, property.city, property.state, property.postal_code]
    .filter(Boolean)
    .join(", ");
};

const getMemberDisplayName = (member) => {
  if (!member) return "";
  const familiarName = member.FamiliarName?.trim();
  const fullName = `${member.FirstName || ""} ${member.LastName || ""}`.trim();
  return familiarName || fullName || "";
};

const getDisplayTitle = (member, property, fallbackAccount = "") => {
  const memberName = getMemberDisplayName(member);
  if (memberName) return memberName;
  if (property?.mailing_name?.trim()) return property.mailing_name.trim();
  if (member?.FullAddress?.trim()) return member.FullAddress.trim();
  if (member?.AlternateAddress?.trim()) return member.AlternateAddress.trim();
  const propertyAddress = buildPropertyAddress(property);
  if (propertyAddress) return propertyAddress;
  return fallbackAccount || "Unknown Account";
};

const getDisplaySubtitle = (member, property, fallbackAccount = "") => {
  const memberName = getMemberDisplayName(member);
  const memberAddress = member?.FullAddress?.trim() || member?.AlternateAddress?.trim() || "";
  const propertyAddress = buildPropertyAddress(property);
  if (memberName && memberAddress) return memberAddress;
  if (memberName && property?.mailing_name?.trim()) return property.mailing_name.trim();
  if (!memberName && property?.mailing_name?.trim() && propertyAddress) return propertyAddress;
  if (!memberName && property?.mailing_name?.trim()) return "Property Account";
  if (!memberName && memberAddress) return "Property Account";
  if (propertyAddress) return propertyAddress;
  return fallbackAccount ? `Member Code: ${fallbackAccount}` : "Property Account";
};

const getDocIcon = (fileType) => {
  if (fileType?.includes("pdf"))
    return <DescriptionIcon fontSize="small" sx={{ color: "#2f63ad" }} />;
  if (fileType?.includes("image"))
    return <ImageIcon fontSize="small" sx={{ color: "#2e7d32" }} />;
  return <InsertDriveFileIcon fontSize="small" sx={{ color: "#6b778c" }} />;
};

const toBoolSetting = (v, def = true) => (v === undefined ? def : Boolean(v));

// ─── Static style objects ─────────────────────────────────────────────────────

const pageCardSx = {
  borderRadius: "18px",
  border: "1px solid rgba(47,99,173,0.08)",
  boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
  backgroundColor: "rgba(255,255,255,0.96)",
};

const statCardSx = {
  ...pageCardSx,
  p: 2.25,
  height: "100%",
};

const secondaryButtonSx = {
  borderRadius: "10px",
  textTransform: "none",
  fontWeight: 700,
  px: 2,
  borderColor: "rgba(47,99,173,0.18)",
  color: "#223142",
  "&:hover": {
    borderColor: "#2f63ad",
    color: "#2f63ad",
    backgroundColor: "rgba(47,99,173,0.04)",
  },
};

const insightPaperSx = {
  p: 1.5,
  borderRadius: "12px",
  border: "1px solid rgba(47,99,173,0.08)",
  backgroundColor: "rgba(47,99,173,0.02)",
};

// ─── Small sub-components ────────────────────────────────────────────────────

const IconBadge = ({ size = 44, children }) => (
  <Box
    sx={{
      width: size,
      height: size,
      borderRadius: "12px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "rgba(47,99,173,0.08)",
      flexShrink: 0,
    }}
  >
    {children}
  </Box>
);

const InsightRow = ({ icon, label }) => (
  <Paper elevation={0} sx={insightPaperSx}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {icon}
      <Typography variant="body2" sx={{ fontWeight: 600, color: "#223142" }}>
        {label}
      </Typography>
    </Box>
  </Paper>
);

const SimpleInsightCard = ({ title, items, emptyText, formatter, getItemHref }) => (
  <Card sx={{ ...pageCardSx, p: 2.5, height: "100%" }}>
    <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>
      {title}
    </Typography>
    <Typography variant="body2" sx={{ color: "#6b778c", mb: 2 }}>
      Quick recent activity overview.
    </Typography>

    {items.length === 0 ? (
      <Typography variant="body2" sx={{ color: "#6b778c" }}>
        {emptyText}
      </Typography>
    ) : (
      <Stack spacing={1.25}>
        {items.map((item, idx) => (
          <Paper
            key={idx}
            elevation={0}
            component={getItemHref ? "a" : "div"}
            href={getItemHref ? getItemHref(item) : undefined}
            onClick={getItemHref ? (e) => { e.preventDefault(); window.location.href = getItemHref(item); } : undefined}
            sx={{
              p: 1.25,
              borderRadius: "12px",
              border: "1px solid rgba(47,99,173,0.08)",
              backgroundColor: "rgba(47,99,173,0.02)",
              textDecoration: "none",
              display: "block",
              ...(getItemHref && {
                cursor: "pointer",
                transition: "background-color 0.15s, border-color 0.15s, box-shadow 0.15s",
                "&:hover": {
                  backgroundColor: "rgba(47,99,173,0.07)",
                  borderColor: "rgba(47,99,173,0.25)",
                  boxShadow: "0 2px 8px rgba(47,99,173,0.12)",
                },
              }),
            }}
          >
            {formatter(item)}
          </Paper>
        ))}
      </Stack>
    )}
  </Card>
);

// ─── DocumentsPanel sub-component ────────────────────────────────────────────

const DocumentsPanel = ({ documents, onPreview, onDownload, navigate }) => {
  const [search, setSearch] = useState("");
  const [sortOrder, setSortOrder] = useState("newest");
  const [categoryFilter, setCategoryFilter] = useState(null);

  // Unique categories from data
  const uniqueCategories = useMemo(
    () => [...new Set(documents.map((d) => d.category_name || "Uncategorized"))].sort(),
    [documents]
  );

  // Apply search + sort + category filter
  const filteredDocs = useMemo(() => {
    const q = search.trim().toLowerCase();
    let result = documents;

    if (q) {
      result = result.filter(
        (d) =>
          String(d.description || "").toLowerCase().includes(q) ||
          String(d.original_filename || "").toLowerCase().includes(q) ||
          String(d.category_name || "").toLowerCase().includes(q)
      );
    }

    if (categoryFilter) {
      result = result.filter(
        (d) => (d.category_name || "Uncategorized") === categoryFilter
      );
    }

    return [...result].sort((a, b) => {
      const da = new Date(a.upload_date || 0);
      const db = new Date(b.upload_date || 0);
      return sortOrder === "newest" ? db - da : da - db;
    });
  }, [documents, search, sortOrder, categoryFilter]);

  // Group filtered docs by category for display
  const groupedDocs = useMemo(
    () =>
      Object.entries(
        filteredDocs.reduce((acc, doc) => {
          const cat = doc.category_name || "Uncategorized";
          if (!acc[cat]) acc[cat] = [];
          acc[cat].push(doc);
          return acc;
        }, {})
      ),
    [filteredDocs]
  );

  if (documents.length === 0) {
    return (
      <Box
        sx={{
          minHeight: 220,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          textAlign: "center",
          color: "grey.500",
        }}
      >
        <InsertDriveFileIcon sx={{ fontSize: 58, mb: 1.5, color: "grey.400" }} />
        <Typography variant="body2" color="text.secondary">
          No documents uploaded yet.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>
        HOA Documents
      </Typography>
      <Typography variant="body2" sx={{ color: "#6b778c", mb: 1.75 }}>
        Browse uploaded files by category.
      </Typography>

      {/* Search + sort toolbar */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center", mb: 1.25 }}>
          <TextField
            size="small"
            placeholder="Search documents…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: "#6b778c" }} />
                </InputAdornment>
              ),
            }}
            sx={{ flex: "1 1 200px", "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
          />

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Sort By</InputLabel>
            <Select
              value={sortOrder}
              label="Sort By"
              onChange={(e) => setSortOrder(e.target.value)}
              sx={{ borderRadius: "10px" }}
            >
              <MenuItem value="newest">Newest First</MenuItem>
              <MenuItem value="oldest">Oldest First</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {uniqueCategories.length > 1 && (
          <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
            <Chip
              label="All"
              size="small"
              onClick={() => setCategoryFilter(null)}
              sx={{
                fontWeight: 600,
                borderRadius: "8px",
                backgroundColor: categoryFilter === null ? "rgba(47,99,173,0.12)" : "rgba(0,0,0,0.05)",
                color: categoryFilter === null ? "#2f63ad" : "#5f6b7a",
                border: categoryFilter === null ? "1px solid rgba(47,99,173,0.3)" : "1px solid transparent",
              }}
            />
            {uniqueCategories.map((cat) => (
              <Chip
                key={cat}
                label={cat}
                size="small"
                onClick={() => setCategoryFilter(cat === categoryFilter ? null : cat)}
                sx={{
                  fontWeight: 600,
                  borderRadius: "8px",
                  backgroundColor: categoryFilter === cat ? "rgba(47,99,173,0.12)" : "rgba(0,0,0,0.05)",
                  color: categoryFilter === cat ? "#2f63ad" : "#5f6b7a",
                  border: categoryFilter === cat ? "1px solid rgba(47,99,173,0.3)" : "1px solid transparent",
                }}
              />
            ))}
          </Box>
        )}
      </Box>

      {filteredDocs.length === 0 ? (
        <Typography variant="body2" sx={{ color: "#6b778c", textAlign: "center", py: 2 }}>
          No documents match your search or filter.
        </Typography>
      ) : (
        <Stack spacing={1.25}>
          {groupedDocs.map(([category, docs]) => (
            <Paper
              key={category}
              elevation={0}
              sx={{ borderRadius: "12px", border: "1px solid rgba(47,99,173,0.08)", overflow: "hidden" }}
            >
              <Box
                sx={{
                  px: 1.5,
                  py: 1.25,
                  backgroundColor: "rgba(47,99,173,0.04)",
                  borderBottom: "1px solid rgba(47,99,173,0.08)",
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {category} ({docs.length})
                </Typography>
              </Box>

              <Box sx={{ p: 1.25 }}>
                {docs.slice(0, 5).map((doc) => (
                  <Box
                    key={doc.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      border: "1px solid #eee",
                      borderRadius: 1.5,
                      p: 1,
                      mb: 0.75,
                      transition: "all 0.2s ease",
                      "&:hover": { boxShadow: 2, borderColor: "rgba(47,99,173,0.22)" },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", flex: 1, gap: 1, minWidth: 0 }}>
                      {getDocIcon(doc.file_type)}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Tooltip title={doc.original_filename}>
                          <Typography variant="body2" noWrap fontWeight={600}>
                            {doc.description || doc.original_filename}
                          </Typography>
                        </Tooltip>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {new Date(doc.upload_date).toLocaleDateString()}
                        </Typography>
                      </Box>
                    </Box>

                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title="Preview">
                        <IconButton size="small" onClick={() => onPreview(doc)}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Download">
                        <IconButton size="small" onClick={() => onDownload(doc)}>
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Box>
                ))}

                {docs.length > 5 && (
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => navigate("/home/filemanager")}
                    startIcon={<FolderOpenIcon />}
                    sx={{ mt: 0.5, textTransform: "none", fontWeight: 600 }}
                  >
                    View all in {category}
                  </Button>
                )}
              </Box>
            </Paper>
          ))}
        </Stack>
      )}
    </Box>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const OwnerDashboard = () => {
  const navigate = useNavigate();
  const { modules } = useContext(ModulesContext);

  const [user, setUser] = useState(null);
  const [memberData, setMemberData] = useState(null);
  const [accountDetailsMap, setAccountDetailsMap] = useState({});
  const [propertiesMap, setPropertiesMap] = useState({});
  const [violationTypes, setViolationTypes] = useState([]);
  const [violations, setViolations] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [previewFile, setPreviewFile] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(0);

  // selectedAccount is URL-driven so sidebar navigation always applies immediately
  const selectedAccount = searchParams.get("account") || null;
  const setSelectedAccount = useCallback((acc) => {
    if (acc) {
      setSearchParams({ account: String(acc) }, { replace: false });
    } else {
      setSearchParams({}, { replace: false });
    }
  }, [setSearchParams]);
  const [loading, setLoading] = useState(true);
  const [accountLoading, setAccountLoading] = useState(false);
  const [dashboardSettings, setDashboardSettings] = useState({
    show_violations: true,
    show_work_orders: true,
    show_architectural_requests: true,
    show_documents: true,
  });

  const username = localStorage.getItem("username");
  const isMobile = useMediaQuery("(max-width:600px)");
  const isTablet = useMediaQuery("(max-width:900px)");

  const selectedProperty = useMemo(() => {
    if (!selectedAccount) return null;
    return propertiesMap[selectedAccount] || null;
  }, [selectedAccount, propertiesMap]);

  // ── Initial data load ──────────────────────────────────────────────────────

  useEffect(() => {
    const fetchInitialData = async () => {
      if (!username) return;

      try {
        setLoading(true);
        const [userRes, typesRes, settingsRes] = await Promise.all([
          api.get(`/users/${username}`),
          api.get(`/getCcrViolationTypes`),
          api.get(`/owner-dashboard-settings`),
        ]);

        setUser(userRes.data);
        setViolationTypes(typesRes.data || []);

        if (settingsRes.data) {
          setDashboardSettings({
            show_violations: toBoolSetting(settingsRes.data.show_violations),
            show_work_orders: toBoolSetting(settingsRes.data.show_work_orders),
            show_architectural_requests: toBoolSetting(settingsRes.data.show_architectural_requests),
            show_documents: toBoolSetting(settingsRes.data.show_documents),
          });
        }

        // Auto-select when only one account, or honour a URL param already set
        const urlAccount = new URLSearchParams(window.location.search).get("account");
        const isMemberRole = localStorage.getItem("roleId") === "3";
        const ownedAccounts = (userRes.data.AssociatedAccounts || []).map(String);

        if (urlAccount) {
          // For member users, reject any account they don't own (URL tampering guard)
          if (isMemberRole && !ownedAccounts.includes(String(urlAccount))) {
            setSearchParams({}, { replace: true });
          }
          // Admins can access any account via URL. leave it as-is
        } else if (ownedAccounts.length === 1) {
          setSelectedAccount(ownedAccounts[0]);
        }
      } catch (error) {
        console.error("Error fetching initial dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [username]);

  // ── Load properties only for the user's accounts ───────────────────────────

  useEffect(() => {
    const fetchProperties = async () => {
      const accounts = user?.AssociatedAccounts;
      if (!accounts?.length) return;

      try {
        const res = await api.get("/properties");
        const map = {};
        (res.data || []).forEach((property) => {
          if (property?.account_id) {
            map[String(property.account_id)] = property;
          }
        });
        setPropertiesMap(map);
      } catch (error) {
        console.error("Error loading properties:", error);
      }
    };

    fetchProperties();
  }, [user?.AssociatedAccounts]);

  // ── Load member details for all associated accounts ────────────────────────

  useEffect(() => {
    const fetchAssociatedAccountDetails = async () => {
      const accounts = user?.AssociatedAccounts || [];
      if (!accounts.length) {
        setAccountDetailsMap({});
        return;
      }

      try {
        const responses = await Promise.allSettled(
          accounts.map((acc) => api.get(`/members/${acc}`))
        );

        const detailsMap = {};
        responses.forEach((result, index) => {
          const acc = String(accounts[index]);
          detailsMap[acc] = result.status === "fulfilled" ? result.value.data : null;
        });

        setAccountDetailsMap(detailsMap);
      } catch (error) {
        console.error("Error fetching associated account details:", error);
      }
    };

    fetchAssociatedAccountDetails();
  }, [user?.AssociatedAccounts]);

  // ── When account changes, reuse cached member data + fetch activity ─────────

  useEffect(() => {
    if (!selectedAccount || !violationTypes.length || !user) return;

    // Block member users from fetching data for accounts they don't own
    const isMemberRole = localStorage.getItem("roleId") === "3";
    const ownedAccounts = (user.AssociatedAccounts || []).map(String);
    if (isMemberRole && !ownedAccounts.includes(String(selectedAccount))) {
      setSearchParams({}, { replace: true });
      return;
    }

    // Reuse already-fetched member data from the accounts map
    const cached = accountDetailsMap[selectedAccount];
    if (cached !== undefined) {
      setMemberData(cached);
    }

    const fetchActivity = async () => {
      try {
        setAccountLoading(true);

        const [violationsRes, workOrdersRes] = await Promise.all([
          dashboardSettings.show_violations
            ? api.get(`/getViolations`, { params: { account_id: selectedAccount } })
            : Promise.resolve({ data: [] }),
          dashboardSettings.show_work_orders
            ? api.get(`/getWorkOrders`, { params: { property_id: selectedAccount } })
            : Promise.resolve({ data: [] }),
        ]);

        const enrichedViolations = (violationsRes.data || []).map((v) => {
          const type = violationTypes.find((t) => t.id === v.ccr_violation_type_id);
          return {
            ...v,
            type: type?.title || "N/A",
            description: type?.resolution_step || "No description",
          };
        });

        setViolations(enrichedViolations);
        setWorkOrders(workOrdersRes.data || []);
      } catch (error) {
        console.error("Error fetching account-related data:", error);
      } finally {
        setAccountLoading(false);
      }
    };

    fetchActivity();
  }, [
    selectedAccount,
    violationTypes,
    accountDetailsMap,
    dashboardSettings.show_violations,
    dashboardSettings.show_work_orders,
  ]);

  // ── Documents (global, not per-account) ───────────────────────────────────

  useEffect(() => {
    const fetchDocuments = async () => {
      if (!dashboardSettings.show_documents) {
        setDocuments([]);
        return;
      }
      try {
        const res = await api.get(`/owner-documents`);
        setDocuments(res.data || []);
      } catch (error) {
        console.error("Error loading documents:", error);
      }
    };

    fetchDocuments();
  }, [dashboardSettings.show_documents]);

  // ── Download handler (no page navigation) ────────────────────────────────

  const handleDownload = useCallback((doc) => {
    const url = `${API_BASE_URL}/uploads/${encodeURIComponent(doc.stored_filename)}`;
    const a = document.createElement("a");
    a.href = url;
    a.download = doc.original_filename || doc.stored_filename;
    a.target = "_blank";
    a.rel = "noreferrer";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, []);

  // ── Derived values ────────────────────────────────────────────────────────

  const documentsByCategory = useMemo(
    () =>
      Object.entries(
        documents.reduce((acc, doc) => {
          const category = doc.category_name || "Uncategorized";
          if (!acc[category]) acc[category] = [];
          acc[category].push(doc);
          return acc;
        }, {})
      ),
    [documents]
  );

  const workOrderOpenCount = workOrders.filter(
    (w) => !CLOSED_WO_STATUSES.has(String(w.status || "").toLowerCase())
  ).length;

  const violationOpenCount = violations.filter(
    (v) => !CLOSED_VIOLATION_STATUSES.has(String(v.status || "").toLowerCase())
  ).length;

  const recentWorkOrders = useMemo(
    () =>
      [...workOrders]
        .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
        .slice(0, 5),
    [workOrders]
  );

  const recentViolations = useMemo(
    () =>
      [...violations]
        .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
        .slice(0, 5),
    [violations]
  );

  const summaryCards = useMemo(
    () =>
      [
        dashboardSettings.show_work_orders && isModuleActive(modules, 1) && {
          title: "Open Work Orders",
          value: workOrderOpenCount,
          subtitle: `${workOrders.length} total`,
          icon: <AssignmentIcon fontSize="small" />,
          backgroundColor: "#2f63ad",
        },
        dashboardSettings.show_violations && isModuleActive(modules, 2) && {
          title: "Open Violations",
          value: violationOpenCount,
          subtitle: `${violations.length} total`,
          icon: <WarningAmberIcon fontSize="small" />,
          backgroundColor: "#d97706",
        },
        dashboardSettings.show_documents && {
          title: "Documents",
          value: documents.length,
          subtitle: `${documentsByCategory.length} categories`,
          icon: <InsertDriveFileIcon fontSize="small" />,
          backgroundColor: "#2f63ad",
        },
        {
          title: "Account",
          value: getDisplayTitle(memberData, selectedProperty, selectedAccount || "?"),
          subtitle: `Code: ${selectedAccount || "-"}`,
          icon: <HomeWorkIcon fontSize="small" />,
          backgroundColor: "#2f63ad",
        },
      ].filter(Boolean),
    [
      dashboardSettings,
      modules,
      workOrderOpenCount,
      workOrders.length,
      violationOpenCount,
      violations.length,
      documents.length,
      documentsByCategory.length,
      memberData,
      selectedProperty,
      selectedAccount,
    ]
  );

  const activityTabs = useMemo(() => {
    const tabs = [];

    if (dashboardSettings.show_violations && isModuleActive(modules, 2)) {
      tabs.push({
        key: "violations",
        label: "Violations",
        content: <ViolationSummary violations={violations} />,
      });
    }

    if (dashboardSettings.show_work_orders && isModuleActive(modules, 1)) {
      tabs.push({
        key: "work_orders",
        label: "Work Orders",
        content: <WorkOrders workOrders={workOrders} propertyId={selectedAccount} />,
      });
    }

    if (dashboardSettings.show_architectural_requests && isModuleActive(modules, 3)) {
      tabs.push({
        key: "architectural_requests",
        label: isMobile ? "Arch. Requests" : "Architectural Requests",
        content: (
          <ArchitecturalRequests accountId={selectedAccount} propertyId={selectedAccount} />
        ),
      });
    }

    if (dashboardSettings.show_documents) {
      tabs.push({
        key: "documents",
        label: "Documents",
        content: (
          <DocumentsPanel
            documents={documents}
            onPreview={setPreviewFile}
            onDownload={handleDownload}
            navigate={navigate}
          />
        ),
      });
    }

    return tabs;
  }, [
    dashboardSettings,
    modules,
    violations,
    workOrders,
    selectedAccount,
    documents,
    documentsByCategory,
    isMobile,
    handleDownload,
    navigate,
  ]);

  // Keep activeTab in range when tabs change (e.g. settings toggle)
  useEffect(() => {
    if (activityTabs.length > 0 && activeTab >= activityTabs.length) {
      setActiveTab(0);
    }
  }, [activityTabs.length, activeTab]);

  const showInsightsSection =
    (dashboardSettings.show_work_orders && isModuleActive(modules, 1)) ||
    (dashboardSettings.show_violations && isModuleActive(modules, 2));

  // ── Loading screen ────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ pt: 2, pb: 6, px: { xs: 1.5, sm: 2, md: 3 } }}>
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Container
      maxWidth="xl"
      sx={{
        pt: { xs: 1.5, md: 2 },
        pb: { xs: 4, md: 6 },
        px: { xs: 1.5, sm: 2, md: 3 },
      }}
    >
      {/* ── Page header ── */}
      <Card
        sx={{
          ...pageCardSx,
          mb: 3,
          p: { xs: 2, md: 2.5 },
          background:
            "linear-gradient(180deg, rgba(47,99,173,0.05) 0%, rgba(255,255,255,0.96) 100%)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          <IconBadge size={44}>
            <HomeWorkIcon sx={{ color: "#2f63ad" }} />
          </IconBadge>

          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant={isMobile ? "h5" : "h4"}
              sx={{ fontWeight: 700, color: "#223142", lineHeight: 1.15 }}
            >
              My Dashboard
            </Typography>
            <Typography variant="body1" sx={{ color: "#6b778c", mt: 0.5 }}>
              Welcome back, {user?.first_name || "Homeowner"}
            </Typography>
          </Box>
        </Box>
      </Card>

      {/* ── Multi-account selector (shown only when no account selected) ── */}
      {user?.AssociatedAccounts?.length > 1 && !selectedAccount && (
        <Card sx={{ ...pageCardSx, p: { xs: 2, md: 2.5 }, mb: 4 }}>
          <Box sx={{ mb: 2.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 0.5 }}>
              <IconBadge size={40}>
                <HomeWorkIcon sx={{ color: "#2f63ad" }} />
              </IconBadge>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>
                Select an Account
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: "#6b778c" }}>
              Choose a property account to view documents, work orders, violations, and activity.
            </Typography>
          </Box>

          <Grid container spacing={2.5}>
            {user.AssociatedAccounts.map((acc) => {
              const accountKey = String(acc);
              const member = accountDetailsMap[accountKey];
              const property = propertiesMap[accountKey];
              const displayTitle = getDisplayTitle(member, property, accountKey);
              const displaySubtitle = getDisplaySubtitle(member, property, accountKey);

              return (
                <Grid item xs={12} sm={6} md={4} key={accountKey}>
                  <Card
                    onClick={() => setSelectedAccount(accountKey)}
                    sx={{
                      borderRadius: "16px",
                      border: "1px solid rgba(47,99,173,0.08)",
                      boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
                      backgroundColor: "rgba(255,255,255,0.96)",
                      p: 2.5,
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      height: "100%",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 12px 30px rgba(0,0,0,0.1)",
                        borderColor: "rgba(47,99,173,0.18)",
                        backgroundColor: "rgba(47,99,173,0.02)",
                      },
                      "&:active": {
                        transform: "translateY(0)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: 1.5,
                      }}
                    >
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="body2" sx={{ color: "#6b778c", mb: 0.75 }}>
                          Property Account
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{
                            color: "#223142",
                            fontWeight: 700,
                            lineHeight: 1.2,
                            wordBreak: "break-word",
                          }}
                        >
                          {displayTitle}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: "#6b778c", mt: 0.75, wordBreak: "break-word" }}
                        >
                          {displaySubtitle}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            mt: 1,
                            color: "#2f63ad",
                            fontWeight: 700,
                            letterSpacing: "0.2px",
                          }}
                        >
                          Member Code: {accountKey}
                        </Typography>
                      </Box>

                      <IconBadge size={40}>
                        <HomeWorkIcon sx={{ color: "#2f63ad" }} />
                      </IconBadge>
                    </Box>

                    <Button
                      variant="outlined"
                      sx={{ ...secondaryButtonSx, mt: 2.25 }}
                      fullWidth
                    >
                      Open Dashboard
                    </Button>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Card>
      )}

      {/* ── Selected account dashboard ── */}
      {selectedAccount && (
        <>
          {/* Back button for multi-account users */}
          {user?.AssociatedAccounts?.length > 1 && (
            <Box sx={{ mb: 2.5 }}>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => {
                  setSelectedAccount(null);
                  setViolations([]);
                  setWorkOrders([]);
                  setMemberData(null);
                  setActiveTab(0);
                }}
                sx={secondaryButtonSx}
              >
                {isMobile ? "Back" : "Back to Account Selection"}
              </Button>
            </Box>
          )}

          {/* Profile card */}
          <Card sx={{ ...pageCardSx, p: { xs: 1.75, sm: 2, md: 2.5 }, mb: 3 }}>
            <ProfileCard user={{ ...user, ...memberData }} />
          </Card>

          {/* Summary stat cards */}
          {summaryCards.length > 0 && (
            <Grid container spacing={2} sx={{ mb: { xs: 3, md: 4 } }}>
              {summaryCards.map((card) => (
                <Grid item xs={6} sm={6} md={3} key={card.title}>
                  <SharedStatCard
                    icon={card.icon}
                    title={card.title}
                    value={card.value}
                    subtitle={card.subtitle}
                    backgroundColor={card.backgroundColor}
                  />
                </Grid>
              ))}
            </Grid>
          )}

          {/* Activity tabs section */}
          <Card sx={{ ...pageCardSx, overflow: "hidden", mb: 4 }}>
            <Box
              sx={{
                px: { xs: 1.5, sm: 2, md: 3 },
                pt: 2.25,
                pb: 1.25,
                borderBottom: "1px solid rgba(47,99,173,0.08)",
                backgroundColor: "rgba(47,99,173,0.02)",
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>
                Account Activity
              </Typography>
              <Typography variant="body2" sx={{ color: "#6b778c", mb: 2 }}>
                Review available account sections.
              </Typography>

              {activityTabs.length > 0 && (
                <Tabs
                  value={activeTab}
                  onChange={(_, value) => setActiveTab(value)}
                  variant="scrollable"
                  scrollButtons="auto"
                  allowScrollButtonsMobile
                  sx={{
                    minHeight: "auto",
                    "& .MuiTab-root": {
                      textTransform: "none",
                      fontWeight: 600,
                      borderRadius: "10px",
                      minHeight: 40,
                      color: "#5f6b7a",
                      mr: { xs: 0.5, md: 1 },
                      px: { xs: 1.25, md: 2 },
                      py: 1,
                      whiteSpace: "nowrap",
                      fontSize: { xs: "0.8rem", md: "0.875rem" },
                    },
                    "& .Mui-selected": {
                      color: "#2f63ad !important",
                      backgroundColor: "rgba(47,99,173,0.08)",
                    },
                    "& .MuiTabs-indicator": { display: "none" },
                  }}
                >
                  {activityTabs.map((tab) => (
                    <Tab key={tab.key} label={tab.label} />
                  ))}
                </Tabs>
              )}
            </Box>

            {/* Render all panels; show only the active one. prevents remount on tab switch */}
            <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, overflowX: "hidden" }}>
              {accountLoading ? (
                <Stack spacing={1.5}>
                  <Skeleton variant="rounded" height={56} />
                  <Skeleton variant="rounded" height={56} />
                  <Skeleton variant="rounded" height={56} />
                </Stack>
              ) : activityTabs.length === 0 ? (
                <Typography variant="body2" sx={{ color: "#6b778c" }}>
                  No sections are available right now.
                </Typography>
              ) : (
                activityTabs.map((tab, idx) => (
                  <Box key={tab.key} sx={{ display: idx === activeTab ? "block" : "none" }}>
                    {tab.content}
                  </Box>
                ))
              )}
            </Box>
          </Card>

          {/* Quick Insights */}
          {showInsightsSection && (
            <Grid container spacing={2.5} sx={{ mb: 2 }}>
              <Grid item xs={12} md={4}>
                <Card sx={{ ...pageCardSx, p: 2.5, height: "100%" }}>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}
                  >
                    Quick Insights
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#6b778c", mb: 2 }}>
                    Snapshot of current account activity.
                  </Typography>

                  <Stack spacing={1.25}>
                    {dashboardSettings.show_work_orders && (
                      <InsightRow
                        icon={<InsightsIcon sx={{ color: "#2f63ad", fontSize: 20 }} />}
                        label={`Open work orders: ${workOrderOpenCount}`}
                      />
                    )}
                    {dashboardSettings.show_violations && (
                      <InsightRow
                        icon={<WarningAmberIcon sx={{ color: "#d97706", fontSize: 20 }} />}
                        label={`Open violations: ${violationOpenCount}`}
                      />
                    )}
                    {dashboardSettings.show_documents && (
                      <InsightRow
                        icon={<ArchitectureIcon sx={{ color: "#2f63ad", fontSize: 20 }} />}
                        label={`Documents available: ${documents.length}`}
                      />
                    )}
                  </Stack>
                </Card>
              </Grid>

              {dashboardSettings.show_work_orders && (
                <Grid item xs={12} sm={6} md={4}>
                  <SimpleInsightCard
                    title="Recent Work Orders"
                    items={recentWorkOrders}
                    emptyText="No recent work orders found."
                    getItemHref={(item) => `/home/memberspage/workorder/${item.order_id}`}
                    formatter={(item) => (
                      <>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: "#2f63ad", display: "block", mb: 0.25 }}>
                          Work Order #{item.order_id}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: "#223142", mb: 0.25 }}>
                          {item.description || item.type_name || "No description"}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#6b778c" }}>
                          {item.status || "Unknown status"}
                        </Typography>
                      </>
                    )}
                  />
                </Grid>
              )}

              {dashboardSettings.show_violations && (
                <Grid item xs={12} sm={6} md={4}>
                  <SimpleInsightCard
                    title="Recent Violations"
                    items={recentViolations}
                    emptyText="No recent violations found."
                    getItemHref={(item) => `/home/memberspage/violation/${item.id}`}
                    formatter={(item) => (
                      <>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: "#2f63ad", display: "block", mb: 0.25 }}>
                          Violation #{item.id}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: "#223142", mb: 0.25 }}>
                          {item.type || "No description"}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#6b778c" }}>
                          {item.status || "Unknown status"}
                        </Typography>
                      </>
                    )}
                  />
                </Grid>
              )}
            </Grid>
          )}

          {/* Document preview modal */}
          <Modal open={Boolean(previewFile)} onClose={() => setPreviewFile(null)}>
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: { xs: "96%", sm: "88%", md: "80%" },
                maxWidth: 960,
                maxHeight: "92vh",
                bgcolor: "background.paper",
                borderRadius: "18px",
                boxShadow: 24,
                p: { xs: 1.5, sm: 2, md: 3 },
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
                sx={{ flexShrink: 0 }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, fontSize: { xs: "1rem", md: "1.25rem" }, minWidth: 0, mr: 1 }}
                  noWrap
                >
                  {previewFile?.original_filename}
                </Typography>
                <IconButton onClick={() => setPreviewFile(null)} size={isMobile ? "small" : "medium"}>
                  <CloseIcon />
                </IconButton>
              </Box>

              <Box sx={{ flex: 1, overflow: "hidden", minHeight: 0 }}>
                {previewFile?.file_type === "application/pdf" || previewFile?.file_type?.includes("pdf") ? (
                  <iframe
                    src={`${API_BASE_URL}${previewFile.file_path}`}
                    title="PDF Preview"
                    width="100%"
                    height="100%"
                    style={{
                      border: "none",
                      borderRadius: "8px",
                      minHeight: isMobile ? "60vh" : "70vh",
                    }}
                  />
                ) : previewFile?.file_type === "text/plain" ? (
                  <iframe
                    src={`${API_BASE_URL}${previewFile.file_path}`}
                    title="Text Preview"
                    width="100%"
                    height="100%"
                    style={{
                      border: "1px solid #ddd",
                      backgroundColor: "#f5f5f5",
                      borderRadius: "8px",
                      minHeight: isMobile ? "60vh" : "70vh",
                    }}
                  />
                ) : previewFile?.original_filename?.toLowerCase().endsWith(".docx") ? (
                  <Typography variant="body2" color="text.secondary">
                    DOCX preview not supported.{" "}
                    <a
                      href={`${API_BASE_URL}${previewFile?.file_path}`}
                      download={previewFile?.original_filename}
                    >
                      Download the file
                    </a>{" "}
                    to view.
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Cannot preview this file type. Please download it to view.
                  </Typography>
                )}
              </Box>
            </Box>
          </Modal>
        </>
      )}
    </Container>
  );
};

export default OwnerDashboard;
